import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Upload, CreditCard, Banknote, FileText, Camera, Loader2, Image, FolderOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';
import EmbeddedPayment from '../components/EmbeddedPayment';

const STEPS = [
  { id: 1, label: 'Informations', icon: FileText },
  { id: 2, label: 'Documents', icon: Upload },
  { id: 3, label: 'Paiement', icon: CreditCard }
];

export const DubaiVisaWorkflowPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const visaTypeName = searchParams.get('type') || 'Visa Visiteur';
  const visaTypeId = searchParams.get('typeId') || 'visitor';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const passportGalleryRef = useRef(null);
  const passportCameraRef = useRef(null);
  const photoGalleryRef = useRef(null);
  const photoCameraRef = useRef(null);

  // Step 1 - Info
  const [info, setInfo] = useState({
    full_name: '', email: '', phone: '',
    travel_date: '', urgency: 'normal', nationality: '', passport_number: ''
  });

  // Step 2 - Documents
  const [passportFile, setPassportFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Step 3
  const [paymentMethod, setPaymentMethod] = useState(null);

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/visa-applications', {
        destination_id: 'dubai',
        visa_type_id: visaTypeId,
        applicant_name: info.full_name,
        applicant_email: info.email,
        applicant_phone: info.phone,
        travel_date: info.travel_date,
        visa_urgency: info.urgency,
        form_data: { nationality: info.nationality, passport_number: info.passport_number }
      });
      setApplicationId(res.data.id);
      setStep(2);
      toast.success('Informations enregistrées');
    } catch (err) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }
    if (type === 'passport') setPassportFile(file);
    else setPhotoFile(file);
  };

  const handleStep2Submit = async () => {
    if (!passportFile || !photoFile) {
      toast.error('Veuillez télécharger les deux documents');
      return;
    }
    setUploading(true);
    try {
      const passForm = new FormData();
      passForm.append('file', passportFile);
      passForm.append('document_type', 'passport');
      await api.post(`/visa-applications/${applicationId}/public-upload?document_type=passport`, passForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const photoForm = new FormData();
      photoForm.append('file', photoFile);
      photoForm.append('document_type', 'passport_photo');
      await api.post(`/visa-applications/${applicationId}/public-upload?document_type=passport_photo`, photoForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setStep(3);
      toast.success('Documents téléchargés');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleCashPayment = async () => {
    setLoading(true);
    try {
      // Update payment method on backend and trigger WhatsApp notification
      await api.post(`/visa-applications/${applicationId}/select-cash-payment`, {
        applicant_name: info.full_name,
        applicant_phone: info.phone
      });
      
      // Calculate amount based on urgency (Normal: $150, Express: $200)
      const amount = info.urgency === 'express' ? 200 : 150;
      
      // Navigate to receipt page with data
      navigate('/visa/receipt', {
        state: {
          reference: applicationId.slice(0, 8).toUpperCase(),
          applicantName: info.full_name,
          applicantEmail: info.email,
          applicantPhone: info.phone,
          visaType: visaTypeName,
          urgency: info.urgency,
          travelDate: info.travel_date,
          amount: amount,
          createdAt: new Date().toISOString()
        }
      });
      
      toast.success('Demande enregistrée !');
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    toast.success('Paiement effectué avec succès !');
    setTimeout(() => navigate('/visa/dubai'), 2000);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  return (
    <div className="bg-[#020617] min-h-screen overflow-x-hidden">
      {/* Header */}
      <section className="py-10 relative">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-xl mx-auto px-4 sm:px-6 relative">
          <button onClick={() => navigate('/visa/dubai')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-6"
            data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-1">
            Visa Dubaï — <span className="text-[#D4AF37] italic">{visaTypeName}</span>
          </h1>
          <p className="text-[#94A3B8] text-xs">Complétez les 3 étapes pour soumettre votre demande</p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="max-w-xl mx-auto px-4 sm:px-6 mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  step > s.id ? 'bg-green-500 text-white' :
                  step === s.id ? 'bg-[#D4AF37] text-black' :
                  'bg-white/10 text-[#64748B]'
                }`} data-testid={`step-${s.id}`}>
                  {step > s.id ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                </div>
                <span className={`text-[10px] font-medium ${
                  step >= s.id ? 'text-white' : 'text-[#64748B]'
                }`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px mx-2 mb-5 ${step > s.id ? 'bg-green-500' : 'bg-white/10'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-xl mx-auto px-4 sm:px-6 pb-16">
        <AnimatePresence mode="wait">
          {/* Step 1 - Info */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="card-luxury p-4">
                <h3 className="text-sm font-semibold text-white mb-4">Informations Personnelles</h3>
                <form onSubmit={handleStep1Submit} className="space-y-3" data-testid="step1-form">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Nom complet *</Label>
                    <Input value={info.full_name} onChange={(e) => setInfo({...info, full_name: e.target.value})}
                      required placeholder="Nom tel qu'il figure sur le passeport" className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#94A3B8] text-xs">Email *</Label>
                      <Input type="email" value={info.email} onChange={(e) => setInfo({...info, email: e.target.value})}
                        required placeholder="votre@email.com" className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-email" />
                    </div>
                    <div>
                      <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
                      <Input value={info.phone} onChange={(e) => setInfo({...info, phone: e.target.value})}
                        required placeholder="+243..." className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-phone" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#94A3B8] text-xs">Nationalité *</Label>
                      <Input value={info.nationality} onChange={(e) => setInfo({...info, nationality: e.target.value})}
                        required placeholder="Ex: Congolaise" className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-nationality" />
                    </div>
                    <div>
                      <Label className="text-[#94A3B8] text-xs">N° Passeport *</Label>
                      <Input value={info.passport_number} onChange={(e) => setInfo({...info, passport_number: e.target.value})}
                        required placeholder="AB1234567" className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-passport" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Date de voyage prévue</Label>
                    <Input type="date" value={info.travel_date} onChange={(e) => setInfo({...info, travel_date: e.target.value})}
                      className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-date" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Urgence</Label>
                    <Select value={info.urgency} onValueChange={(v) => setInfo({...info, urgency: v})}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs h-9" data-testid="info-urgency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-white/10">
                        <SelectItem value="normal">Normal — 150$</SelectItem>
                        <SelectItem value="express">Express — 200$</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <button type="submit" disabled={loading} className="w-full btn-gold py-3 text-xs flex items-center justify-center gap-2" data-testid="step1-next">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Suivant <ArrowRight className="w-3 h-3" /></>}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* Step 2 - Documents */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="card-luxury p-4 space-y-4">
                <h3 className="text-sm font-semibold text-white">Téléchargement des Documents</h3>
                <p className="text-[#94A3B8] text-xs">Veuillez télécharger une copie de votre passeport et une photo d'identité format passeport.</p>

                {/* Passport Upload */}
                <div className="border border-dashed border-white/20 rounded-xl p-4">
                  {/* Hidden inputs */}
                  <input ref={passportGalleryRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect(e, 'passport')} />
                  <input ref={passportCameraRef} type="file" className="hidden" accept="image/*" capture="environment"
                    onChange={(e) => handleFileSelect(e, 'passport')} />
                  
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${passportFile ? 'bg-green-500/20 border-green-500/30' : 'bg-white/5 border-white/10'} border`}>
                      {passportFile ? <Check className="w-5 h-5 text-green-400" /> : <FileText className="w-5 h-5 text-[#94A3B8]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white">Copie du Passeport *</p>
                      <p className="text-[10px] text-[#64748B] truncate">
                        {passportFile ? passportFile.name : 'PDF, JPG ou PNG — Max 10MB'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Choice buttons */}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => passportGalleryRef.current?.click()}
                      className="flex-1 py-2 px-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center justify-center gap-2"
                      data-testid="passport-gallery">
                      <FolderOpen className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-white">Galerie</span>
                    </button>
                    <button type="button" onClick={() => passportCameraRef.current?.click()}
                      className="flex-1 py-2 px-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center justify-center gap-2"
                      data-testid="passport-camera">
                      <Camera className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-white">Caméra</span>
                    </button>
                  </div>
                </div>

                {/* Photo Upload */}
                <div className="border border-dashed border-white/20 rounded-xl p-4">
                  {/* Hidden inputs */}
                  <input ref={photoGalleryRef} type="file" className="hidden" accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect(e, 'photo')} />
                  <input ref={photoCameraRef} type="file" className="hidden" accept="image/*" capture="user"
                    onChange={(e) => handleFileSelect(e, 'photo')} />
                  
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${photoFile ? 'bg-green-500/20 border-green-500/30' : 'bg-white/5 border-white/10'} border`}>
                      {photoFile ? <Check className="w-5 h-5 text-green-400" /> : <Image className="w-5 h-5 text-[#94A3B8]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white">Photo Passeport *</p>
                      <p className="text-[10px] text-[#64748B] truncate">
                        {photoFile ? photoFile.name : 'JPG ou PNG — Fond blanc, format 4x4'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Choice buttons */}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => photoGalleryRef.current?.click()}
                      className="flex-1 py-2 px-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center justify-center gap-2"
                      data-testid="photo-gallery">
                      <FolderOpen className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-white">Galerie</span>
                    </button>
                    <button type="button" onClick={() => photoCameraRef.current?.click()}
                      className="flex-1 py-2 px-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center justify-center gap-2"
                      data-testid="photo-camera">
                      <Camera className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-white">Caméra</span>
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="flex-1 btn-glass py-3 text-xs text-white">Retour</button>
                  <button onClick={handleStep2Submit} disabled={uploading || !passportFile || !photoFile}
                    className="flex-1 btn-gold py-3 text-xs flex items-center justify-center gap-2" data-testid="step2-next">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Suivant <ArrowRight className="w-3 h-3" /></>}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3 - Payment */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="card-luxury p-4 space-y-4">
                <h3 className="text-sm font-semibold text-white">Paiement</h3>

                {/* Summary */}
                <div className="bg-white/5 rounded-xl p-3 divide-y divide-white/10">
                  <div className="flex justify-between py-2">
                    <span className="text-[#94A3B8] text-xs">Type de visa</span>
                    <span className="text-white text-xs font-medium">{visaTypeName}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#94A3B8] text-xs">Urgence</span>
                    <span className="text-white text-xs font-medium">{info.urgency === 'express' ? 'Express' : 'Normal'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#94A3B8] text-xs">Documents</span>
                    <span className="text-green-400 text-xs font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Téléchargés</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#D4AF37] text-xs font-semibold">Montant Total</span>
                    <span className="text-[#D4AF37] text-sm font-bold">{info.urgency === 'express' ? '200' : '150'}$</span>
                  </div>
                </div>

                <p className="text-[#94A3B8] text-xs">Choisissez votre mode de paiement :</p>

                {/* Payment Options */}
                <div className="space-y-3">
                  <div
                    className="w-full p-4 rounded-xl border border-white/10 bg-white/5 opacity-50 cursor-not-allowed flex items-center gap-3"
                    data-testid="pay-card">
                    <CreditCard className="w-5 h-5 text-[#94A3B8]" />
                    <div className="text-left flex-1">
                      <p className="text-xs font-medium text-white">Payer par Carte</p>
                      <p className="text-[10px] text-[#64748B]">Bientôt disponible</p>
                    </div>
                    <span className="text-[10px] bg-white/10 text-[#94A3B8] px-2 py-1 rounded">Indisponible</span>
                  </div>

                  <button onClick={() => setPaymentMethod('cash')}
                    className={`w-full p-4 rounded-xl border transition-all flex items-center gap-3 ${
                      paymentMethod === 'cash' ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`} data-testid="pay-cash">
                    <Banknote className={`w-5 h-5 ${paymentMethod === 'cash' ? 'text-[#D4AF37]' : 'text-[#94A3B8]'}`} />
                    <div className="text-left">
                      <p className="text-xs font-medium text-white">Payer en Espèces</p>
                      <p className="text-[10px] text-[#64748B]">À nos bureaux — Confirmation via WhatsApp</p>
                    </div>
                  </button>
                </div>

                {/* Cash Payment Button */}
                {paymentMethod === 'cash' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="pt-4"
                  >
                    <button onClick={handleCashPayment}
                      disabled={loading}
                      className="w-full btn-gold py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="confirm-cash">
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Traitement en cours...
                        </>
                      ) : (
                        <>
                          <Banknote className="w-5 h-5" />
                          Confirmer le paiement en espèces
                        </>
                      )}
                    </button>
                    <p className="text-[10px] text-[#64748B] text-center mt-2">
                      Vous recevrez un WhatsApp avec les adresses de nos bureaux
                    </p>
                  </motion.div>
                )}

                {/* Back button */}
                <button onClick={() => setStep(2)} className="w-full btn-glass py-3 text-xs text-white mt-4">
                  <ArrowLeft className="w-3 h-3 inline mr-2" />
                  Retour
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DubaiVisaWorkflowPage;
